import { useEffect, useMemo, useState } from "react";

type LogKind = "csp" | "frame" | "coop" | "console" | "message" | "info" | "tool" | "pointer";

type LogEntry = {
  id: number;
  kind: LogKind;
  ts: number;
  text: string;
};

/**
 * Dev-only debug overlay for diagnosing Lovable Visual Edit issues.
 *
 * Shows:
 * - Whether the page is running inside an iframe and the parent origin.
 * - Live CSP violations (`securitypolicyviolation` events).
 * - X-Frame / COOP related console errors (pattern match).
 * - Any postMessage traffic between parent (Lovable editor) and this frame,
 *   which is the visible signal of a successful overlay handshake.
 *
 * Enable with `?ve-debug=1` in the URL (or `#ve-debug`). Only mounts client-side.
 */
export function VisualEditDebugPanel() {
  const [enabled, setEnabled] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const [handshake, setHandshake] = useState<"pending" | "received">("pending");
  const [activeTool, setActiveTool] = useState<string>("(none)");
  const [lastToolAt, setLastToolAt] = useState<number | null>(null);
  const [pointerCount, setPointerCount] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const on =
      url.searchParams.get("ve-debug") === "1" ||
      window.location.hash.includes("ve-debug");
    setEnabled(on);
  }, []);

  const inIframe = useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.self !== window.top;
    } catch {
      // Cross-origin access blocked = we ARE inside a cross-origin iframe.
      return true;
    }
  }, []);

  const parentOrigin = useMemo(() => {
    if (typeof document === "undefined") return "";
    try {
      return document.referrer ? new URL(document.referrer).origin : "(none)";
    } catch {
      return "(unavailable)";
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let counter = 0;
    const push = (kind: LogKind, text: string) => {
      counter += 1;
      setLogs((prev) => [...prev.slice(-199), { id: counter, kind, ts: Date.now(), text }]);
    };

    push("info", `in-iframe=${inIframe} parent=${parentOrigin} origin=${window.location.origin}`);

    const onCsp = (e: SecurityPolicyViolationEvent) => {
      push(
        "csp",
        `CSP block: directive="${e.effectiveDirective || e.violatedDirective}" blocked="${e.blockedURI}" source="${e.sourceFile || ""}:${e.lineNumber || 0}"`,
      );
    };
    window.addEventListener("securitypolicyviolation", onCsp);

    const onMessage = (e: MessageEvent) => {
      const origin = e.origin || "(null)";
      // Strict origin allowlist: only trust editor traffic from real Lovable
      // hosts. Messages from anywhere else are logged but never acted upon.
      let isLovable = false;
      try {
        const hostname = new URL(origin).hostname;
        isLovable =
          hostname === "lovable.app" ||
          hostname.endsWith(".lovable.app") ||
          hostname === "lovable.dev" ||
          hostname.endsWith(".lovable.dev") ||
          hostname === "lovableproject.com" ||
          hostname.endsWith(".lovableproject.com");
      } catch {
        isLovable = false;
      }
      let preview = "";
      try {
        preview = typeof e.data === "string" ? e.data : JSON.stringify(e.data);
      } catch {
        preview = "(unserializable)";
      }
      preview = preview.slice(0, 160);
      push("message", `postMessage from ${origin}${isLovable ? " [lovable]" : ""}: ${preview}`);
      if (isLovable) setHandshake("received");

      // Only mutate tool state for verified Lovable editor origins; anything
      // else is untrusted noise that must not drive panel state.
      if (!isLovable) return;
      const data = e.data as unknown;
      if (data && typeof data === "object") {
        const d = data as Record<string, unknown>;
        const candidates = [
          d.tool,
          d.activeTool,
          d.mode,
          d.selectedTool,
          (d.payload as Record<string, unknown> | undefined)?.tool,
          (d.payload as Record<string, unknown> | undefined)?.mode,
          (d.payload as Record<string, unknown> | undefined)?.activeTool,
        ].filter((v): v is string => typeof v === "string" && v.length > 0);
        const type = typeof d.type === "string" ? d.type : "";
        const toolFromType = /TOOL|MODE|SELECT|EDIT|DRAW|COMMENT|ANNOTATE/i.test(type) ? type : "";
        const next = candidates[0] || toolFromType;
        if (next) {
          setActiveTool(next);
          setLastToolAt(Date.now());
          push("tool", `tool signal: ${next} (from ${origin})`);
        }
      }
    };
    window.addEventListener("message", onMessage);

    // Log pointer traffic so we can see whether clicks reach the iframe
    // (if editor overlay eats them at the parent, we get 0 here).
    const onPointerDown = (e: PointerEvent) => {
      setPointerCount((n) => n + 1);
      const t = e.target as Element | null;
      const tag = t?.tagName?.toLowerCase() || "?";
      const cls = (t?.getAttribute?.("class") || "").slice(0, 60);
      push("pointer", `pointerdown ${tag}${cls ? "." + cls : ""} @${Math.round(e.clientX)},${Math.round(e.clientY)}`);
      // Echo up to the parent so we can also see it in the editor bridge log.
      try {
        window.parent?.postMessage(
          { source: "ve-debug", type: "IFRAME_POINTERDOWN", x: e.clientX, y: e.clientY, tag },
          "*",
        );
      } catch {
        // ignore
      }
    };
    window.addEventListener("pointerdown", onPointerDown, true);

    const origError = console.error;
    const origWarn = console.warn;
    const capture = (label: "error" | "warn") => (...args: unknown[]) => {
      const text = args
        .map((a) => {
          if (a instanceof Error) return a.message;
          if (typeof a === "string") return a;
          try {
            return JSON.stringify(a);
          } catch {
            return String(a);
          }
        })
        .join(" ");
      const t = text.toLowerCase();
      if (t.includes("content security policy") || t.includes("refused to")) push("csp", `[${label}] ${text}`);
      else if (t.includes("x-frame-options") || t.includes("frame-ancestors")) push("frame", `[${label}] ${text}`);
      else if (t.includes("cross-origin-opener-policy") || t.includes("coop")) push("coop", `[${label}] ${text}`);
      else if (t.includes("lovable") || t.includes("gpteng") || t.includes("visual edit")) push("console", `[${label}] ${text}`);
      if (label === "error") origError(...args);
      else origWarn(...args);
    };
    console.error = capture("error");
    console.warn = capture("warn");

    // If no lovable postMessage arrives within 5s, flag it.
    const t = window.setTimeout(() => {
      setHandshake((h) => {
      if (h === "pending") push("info", "No postMessage from a lovable.* origin within 5s - overlay handshake failed.");
        return h;
      });
    }, 5000);

    return () => {
      window.removeEventListener("securitypolicyviolation", onCsp);
      window.removeEventListener("message", onMessage);
      window.removeEventListener("pointerdown", onPointerDown, true);
      console.error = origError;
      console.warn = origWarn;
      window.clearTimeout(t);
    };
  }, [enabled, inIframe, parentOrigin]);

  if (!enabled) return null;

  const badgeColor: Record<LogKind, string> = {
    csp: "#ef4444",
    frame: "#f97316",
    coop: "#eab308",
    console: "#a3a3a3",
    message: "#22c55e",
    info: "#38bdf8",
    tool: "#a855f7",
    pointer: "#f472b6",
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: 12,
        right: 12,
        zIndex: 2147483647,
        width: collapsed ? 220 : 460,
        maxHeight: collapsed ? 44 : 420,
        background: "rgba(9,9,11,0.94)",
        color: "#e5e7eb",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 11,
        border: "1px solid #27272a",
        borderRadius: 8,
        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
        overflow: "hidden",
        pointerEvents: "none",
      }}
      role="region"
      aria-label="Visual Edit debug panel"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 10px",
          background: "#18181b",
          borderBottom: "1px solid #27272a",
          cursor: "pointer",
          pointerEvents: "auto",
        }}
        onClick={() => setCollapsed((c) => !c)}
      >
        <span style={{ fontWeight: 600 }}>
          Visual Edit debug{" "}
          <span
            style={{
              display: "inline-block",
              marginLeft: 6,
              padding: "1px 6px",
              borderRadius: 4,
              background: handshake === "received" ? "#166534" : "#7f1d1d",
              color: "#fff",
              fontSize: 10,
            }}
          >
            {handshake === "received" ? "handshake ✓" : "no handshake"}
          </span>
        </span>
        <span style={{ opacity: 0.6 }}>{collapsed ? "▲" : "▼"}</span>
      </div>
      {!collapsed ? (
        <div style={{ padding: 10, overflow: "auto", maxHeight: 360 }}>
          <div style={{ marginBottom: 8, opacity: 0.8 }}>
            iframe: <b>{String(inIframe)}</b> · parent: <b>{parentOrigin}</b>
            <br />
            origin: <b>{typeof window !== "undefined" ? window.location.origin : ""}</b>
          </div>
          <div
            style={{
              marginBottom: 8,
              padding: "6px 8px",
              background: activeTool === "(none)" ? "#3f1d1d" : "#3b0764",
              border: "1px solid #52525b",
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <span>
              active tool:{" "}
              <b style={{ color: activeTool === "(none)" ? "#fca5a5" : "#e9d5ff" }}>{activeTool}</b>
              {lastToolAt ? (
                <span style={{ opacity: 0.6 }}>
                  {" "}
                  ({Math.max(0, Math.round((Date.now() - lastToolAt) / 1000))}s ago)
                </span>
              ) : null}
            </span>
            <span style={{ opacity: 0.7 }}>pointerdown: <b>{pointerCount}</b></span>
          </div>
          {logs.length === 0 ? (
            <div style={{ opacity: 0.5 }}>No events captured yet…</div>
          ) : (
            logs
              .slice()
              .reverse()
              .map((l) => (
                <div key={l.id} style={{ marginBottom: 4, lineHeight: 1.35 }}>
                  <span
                    style={{
                      display: "inline-block",
                      minWidth: 54,
                      marginRight: 6,
                      padding: "0 4px",
                      background: badgeColor[l.kind],
                      color: "#0b0b0b",
                      borderRadius: 3,
                      textAlign: "center",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: 9,
                    }}
                  >
                    {l.kind}
                  </span>
                  <span style={{ wordBreak: "break-word" }}>{l.text}</span>
                </div>
              ))
          )}
        </div>
      ) : null}
    </div>
  );
}